import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import color from 'styles/colors';
import { useCookies } from 'react-cookie';
import { respondTo } from 'styles/helper';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { fadein } from 'styles/animations';
import { useRouter } from 'next/router';
import { Profile } from 'lib/types/profileType';
import { profileConfig } from 'lib/utils/configs';
import { handleTrackEvent, Events } from 'lib/analytics/trackCalls';
import { Config } from 'lib/types/configType';
import { useFlowManager } from 'lib/hooks/';
import { AppContext } from 'context/GlobalContextProvider';
import useSetUserLoggedInOrRegistered from 'lib/utils/useSetUserLoggedInOrRegistered';
import { setError, setFlowStage } from 'context/state/reducer';
import MobileApps from 'components/MobileApps';
import { SEO, Messages } from 'components/common';
import {
  ACCOUNT_COOKIE_NAME_NATION,
  COOKIE_CODE_INFO,
  FLOW_STAGE_MVPD_LINK,
  ROUTE_HOME,
} from 'constants/index';

const getNationAuthData = (): AnyObject => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(ACCOUNT_COOKIE_NAME_NATION) || '{}');
  } catch {
    return {};
  }
};
import { setConfigToProfile, tokenValid } from 'lib/utils/helper';

type Props = {
  config: Config;
  profile: Profile;
};

const AccountSuccess = ({ profile }: Props): JSX.Element | null => {
  const router = useRouter();
  const { dispatch } = useContext(AppContext);
  const [cookies] = useCookies([COOKIE_CODE_INFO]);
  useSetUserLoggedInOrRegistered({
    profile,
  });

  const { isNation } = useFlowManager();

  useEffect(() => {
    if (!isNation) {
      router?.push(ROUTE_HOME);
    }
  }, [isNation, router]);

  useEffect(() => {
    if (!profile?.checkAdobeAuthN) return;
    let updatedProfile;
    const urlParams = new URLSearchParams(window.location.search);
    const regCode = urlParams.get('regCode') || '';
    const validateAuth = async () => {
      const nationAccessToken = getNationAuthData()?.accessToken;
      if (!nationAccessToken) return;

      updatedProfile = (await setConfigToProfile(profile, {
        appName: profileConfig.appName,
        appVersion: profileConfig.appVersion,
        accessToken: nationAccessToken,
      })) as Profile;

      const token = jwtDecode<JwtPayload>(nationAccessToken);
      try {
        if (tokenValid(token)) {
          const { foxLoginStatus } = await updatedProfile?.setRegCodeStatus?.({
            regCode,
            firstScreenVersion: '',
            secondScreenVersion: '',
            status: {
              loginType: 'FOX',
              state: 'OK',
            },
          });
          const { mvpdLoginStatus } = await updatedProfile?.setRegCodeStatus?.({
            regCode,
            firstScreenVersion: '',
            secondScreenVersion: '',
            status: {
              loginType: 'MVPD',
              state: 'ABORTED',
            },
          });

          if (foxLoginStatus === 'OK' && mvpdLoginStatus === 'ABORTED') {
            dispatch(setFlowStage(FLOW_STAGE_MVPD_LINK));
            handleTrackEvent({
              eventName: Events.PROFILE_ACCESS_ACTIVATION_COMPLETED,
            });
            return;
          } else {
            // await router.push(ROUTE_HOME);
          }
        }
        return;
      } catch (e) {
        dispatch(setError(e));
        await router.push(ROUTE_HOME);
      }
    };
    validateAuth();
  }, [profile, cookies, dispatch, router, isNation]);

  if (!isNation) return null;

  return (
    <>
      <SEO title="Account Success" />
      <Root>
        <Messages isNationAuthSuccess />
        <MobileApps />
      </Root>
    </>
  );
};

export default AccountSuccess;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 2rem auto 0;
  height: 100%;
  font-size: 16px;
  text-align: center;
  ${fadein};
  ${respondTo.bp_768`
    max-width: 500px;
    margin: auto;
  `}

  h1 {
    font-size: 19px;
    font-weight: 600;
    ${respondTo.bp_768`
      font-size: 30px;
      letter-spacing: -0.38px;
      margin: 0 0 3rem;
  `}
  }
  .message {
    color: ${color.white};
    font-size: 15px;
    font-family: 'Graphik', serif;
    font-weight: 400;
    letter-spacing: -0.27px;
    line-height: 20px;
    opacity: 0.5;
    ${respondTo.bp_768`
      font-size: 24px;
      line-height: 30px;
      letter-spacing: -0.3px;
  `}
  }
`;
